import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useConfirm } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { pluralise } from 'lib/pluralise';
import { without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  doFieldSettingsLinkCustomer,
} from 'store/actions';

export const FieldSettingsLinkCustomerModal: React.FC<
  ModalScreenProps<ModalScreens.FieldSettingsSectionLinkCustomer>
> = ({ navigation, route }) => {
  const { sectionId, sectionName, organisations } = route.params;
  const confirm = useConfirm();
  const dispatch = useDispatch();

  const [organisationIds, setOrganisationIds] = React.useState<string[]>([]);

  const handleSave = () =>
    confirm({
      title: 'Link Section',
      confirmText: 'Link',
      text: `Are you sure you want to link this section to ${pluralise(
        organisationIds,
        'this organisation',
        '{x} organisations',
      )}${
        organisationIds.length > 50
          ? '. This may take a very very long time to finish'
          : organisationIds.length > 25
            ? '. This may take a very long time to finish'
            : organisationIds.length > 9
              ? '. This may take a while to finish'
              : ''
      }`,
      onConfirm: () =>
        dispatch(
          doFieldSettingsLinkCustomer({
            id: sectionId,
            clientIds: organisationIds,
          }),
        ),
    });

  const handleToggleOrganisation = (newOrganisationId: string) =>
    setOrganisationIds((state) =>
      state.includes(newOrganisationId)
        ? without(state, newOrganisationId)
        : [...state, newOrganisationId],
    );

  const handleBack = () => {
    if (organisationIds.length) {
      confirm({
        text: 'Your selection will be lost if you continue',
        confirmText: 'Discard Changes',
        cancelText: 'Cancel',
        destructive: true,
        onConfirm: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !organisationIds.length });
  }, [navigation, organisationIds]);
  useEsc(handleBack);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Link Section"
          subheading={sectionName}
          onPressCancelButton={handleBack}
          onPressSaveButton={handleSave}
          saveButtonText="Link"
        />
      }
    >
      <Card label="Choose where to copy this section to">
        {organisations.map((organisation) => (
          <CardRow
            key={organisation.id}
            label={organisation.name}
            onPress={() => handleToggleOrganisation(organisation.id)}
            checkedValue={organisationIds.includes(organisation.id)}
          />
        ))}
      </Card>
    </PageScrollView>
  );
};
